/**
 * Landing page for the Meridian mobile app.
 *
 * Full-screen immersive intro with animated connection lines,
 * a glowing brand mark, and clear calls to action. Designed
 * for mobile-first with the warm amber primary palette.
 * Replaces the placeholder Dashboard view.
 */
import { useState } from "react";
import { usePlatform } from "../hooks/usePlatform";
import { Button } from "../ui/Button";

export function Landing() {
    const { isNative, haptic } = usePlatform();
    const [entered, setEntered] = useState(false);

    function handleGetStarted() {
        haptic();
        setEntered(true);
    }

    if (entered) {
        return <ComingSoon onBack={() => setEntered(false)} />;
    }

    return (
        <main className="relative flex flex-1 flex-col overflow-hidden">
            <ConnectionLines />

            <div className="relative z-10 flex flex-1 flex-col items-center justify-center px-8">
                <BrandMark />

                <h1
                    className="mt-10 text-3xl font-light tracking-widest uppercase"
                    style={{ color: "oklch(0.92 0.01 250)" }}
                >
                    Meridian
                </h1>

                <p
                    className="mt-3 max-w-[260px] text-center text-sm leading-relaxed"
                    style={{ color: "oklch(0.50 0.03 250)" }}
                >
                    Map your world. Every relationship, every moment, one living network.
                </p>

                <div className="mt-14 flex w-full max-w-[280px] flex-col gap-3">
                    <Button
                        size="lg"
                        className="w-full rounded-xl text-sm font-medium tracking-wide"
                        onClick={handleGetStarted}
                    >
                        Get Started
                    </Button>
                    <Button
                        variant="ghost"
                        size="lg"
                        className="w-full rounded-xl text-sm tracking-wide"
                        style={{ color: "oklch(0.55 0.03 250)" }}
                    >
                        Sign In
                    </Button>
                </div>
            </div>

            <footer className="relative z-10 flex flex-col items-center pb-6">
                <div
                    className="rounded-full px-4 py-1.5"
                    style={{ background: "oklch(0.15 0.02 250)" }}
                >
                    <p
                        className="text-[10px] tracking-widest uppercase"
                        style={{ color: "oklch(0.38 0.02 250)" }}
                    >
                        v0.1.0 {isNative ? "native" : "web"}
                    </p>
                </div>
            </footer>
        </main>
    );
}

function BrandMark() {
    return (
        <div className="relative flex h-44 w-44 items-center justify-center">
            {/* Outer orbital rings */}
            <div
                className="absolute inset-0 rounded-full"
                style={{
                    border: "1px solid oklch(0.25 0.03 45 / 0.3)",
                    animation: "spin-slow 30s linear infinite",
                }}
            />
            <div
                className="absolute inset-3 rounded-full"
                style={{
                    border: "1px solid oklch(0.30 0.04 45 / 0.2)",
                    animation: "spin-slow 20s linear infinite reverse",
                }}
            />
            <div
                className="absolute inset-7 rounded-full"
                style={{
                    border: "1px solid oklch(0.35 0.05 45 / 0.15)",
                    animation: "spin-slow 25s linear infinite",
                }}
            />

            {/* Orbital dots */}
            <div
                className="absolute top-0 left-1/2 h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full"
                style={{
                    background: "oklch(0.65 0.15 45)",
                    boxShadow: "0 0 8px oklch(0.65 0.15 45 / 0.6)",
                    animation: "spin-slow 30s linear infinite",
                    transformOrigin: "50% 88px",
                }}
            />
            <div
                className="absolute bottom-2 right-2 h-1 w-1 rounded-full"
                style={{
                    background: "oklch(0.55 0.12 45)",
                    boxShadow: "0 0 6px oklch(0.55 0.12 45 / 0.5)",
                    animation: "spin-slow 20s linear infinite reverse",
                    transformOrigin: "center",
                }}
            />

            {/* Center glow */}
            <div
                className="absolute inset-10 rounded-full"
                style={{
                    background:
                        "radial-gradient(circle, oklch(0.55 0.15 45 / 0.15) 0%, transparent 70%)",
                }}
            />

            {/* Center letter */}
            <span
                className="relative text-6xl font-extralight"
                style={{
                    color: "oklch(0.75 0.13 45)",
                    textShadow: "0 0 30px oklch(0.65 0.15 45 / 0.3)",
                }}
            >
                M
            </span>
        </div>
    );
}

function ConnectionLines() {
    return (
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
            {/* Background gradient */}
            <div
                className="absolute inset-0"
                style={{
                    background:
                        "radial-gradient(ellipse at 50% 35%, oklch(0.16 0.04 45 / 0.4), transparent 60%), radial-gradient(ellipse at 50% 100%, oklch(0.14 0.03 250 / 0.5), transparent 50%)",
                }}
            />

            {/* Animated connection lines */}
            <svg className="absolute inset-0 h-full w-full" xmlns="http://www.w3.org/2000/svg">
                <defs>
                    <linearGradient id="line-grad-1" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="oklch(0.55 0.12 45 / 0)" />
                        <stop offset="50%" stopColor="oklch(0.55 0.12 45 / 0.15)" />
                        <stop offset="100%" stopColor="oklch(0.55 0.12 45 / 0)" />
                    </linearGradient>
                    <linearGradient id="line-grad-2" x1="100%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="oklch(0.45 0.08 250 / 0)" />
                        <stop offset="50%" stopColor="oklch(0.45 0.08 250 / 0.1)" />
                        <stop offset="100%" stopColor="oklch(0.45 0.08 250 / 0)" />
                    </linearGradient>
                </defs>

                {/* Diagonal connection paths */}
                <line
                    x1="10%" y1="15%" x2="45%" y2="40%"
                    stroke="url(#line-grad-1)" strokeWidth="0.5"
                    style={{ animation: "fade-line 4s ease-in-out infinite" }}
                />
                <line
                    x1="90%" y1="20%" x2="55%" y2="42%"
                    stroke="url(#line-grad-1)" strokeWidth="0.5"
                    style={{ animation: "fade-line 4s ease-in-out infinite 1s" }}
                />
                <line
                    x1="15%" y1="80%" x2="48%" y2="55%"
                    stroke="url(#line-grad-2)" strokeWidth="0.5"
                    style={{ animation: "fade-line 4s ease-in-out infinite 2s" }}
                />
                <line
                    x1="85%" y1="75%" x2="52%" y2="50%"
                    stroke="url(#line-grad-2)" strokeWidth="0.5"
                    style={{ animation: "fade-line 4s ease-in-out infinite 0.5s" }}
                />
                <line
                    x1="5%" y1="50%" x2="42%" y2="45%"
                    stroke="url(#line-grad-1)" strokeWidth="0.5"
                    style={{ animation: "fade-line 4s ease-in-out infinite 3s" }}
                />
                <line
                    x1="95%" y1="55%" x2="58%" y2="48%"
                    stroke="url(#line-grad-1)" strokeWidth="0.5"
                    style={{ animation: "fade-line 4s ease-in-out infinite 1.5s" }}
                />

                {/* Node dots at endpoints */}
                <circle cx="10%" cy="15%" r="2" fill="oklch(0.45 0.08 45 / 0.4)" />
                <circle cx="90%" cy="20%" r="2.5" fill="oklch(0.50 0.10 45 / 0.3)" />
                <circle cx="15%" cy="80%" r="1.5" fill="oklch(0.40 0.06 250 / 0.3)" />
                <circle cx="85%" cy="75%" r="2" fill="oklch(0.45 0.08 250 / 0.25)" />
                <circle cx="5%" cy="50%" r="1.5" fill="oklch(0.40 0.06 45 / 0.3)" />
                <circle cx="95%" cy="55%" r="2" fill="oklch(0.45 0.08 45 / 0.2)" />
            </svg>
        </div>
    );
}

function ComingSoon({ onBack }: { onBack: () => void }) {
    return (
        <main className="relative flex flex-1 flex-col items-center justify-center px-8">
            <div
                className="absolute inset-0"
                style={{
                    background:
                        "radial-gradient(ellipse at 50% 40%, oklch(0.16 0.04 45 / 0.3), transparent 60%)",
                }}
            />

            <div className="relative z-10 flex flex-col items-center">
                <div
                    className="flex h-16 w-16 items-center justify-center rounded-2xl"
                    style={{
                        background: "oklch(0.55 0.15 45 / 0.1)",
                        border: "1px solid oklch(0.55 0.15 45 / 0.2)",
                    }}
                >
                    <span
                        className="text-2xl font-light"
                        style={{ color: "oklch(0.70 0.13 45)" }}
                    >
                        M
                    </span>
                </div>

                <h2
                    className="mt-8 text-xl font-medium tracking-wide"
                    style={{ color: "oklch(0.90 0.01 250)" }}
                >
                    Something great is coming
                </h2>

                <p
                    className="mt-3 max-w-[240px] text-center text-sm leading-relaxed"
                    style={{ color: "oklch(0.45 0.02 250)" }}
                >
                    We're building the future of how you manage your relationships.
                </p>

                <Button
                    variant="outline"
                    size="md"
                    className="mt-10 rounded-xl px-8"
                    onClick={onBack}
                    style={{
                        borderColor: "oklch(0.25 0.03 250)",
                        color: "oklch(0.55 0.03 250)",
                    }}
                >
                    Go Back
                </Button>
            </div>
        </main>
    );
}
